package com.shadowings.gero.presentation.component.quiz

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.graphics.rememberGraphicsLayer
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import androidx.navigation.compose.rememberNavController
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.QuizQuestionType
import com.shadowings.gero.domain.model.TextTranslationContent
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.domain.model.WrongAnswerRecord
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.quizInputCorrectBorder
import com.shadowings.gero.presentation.ui.quizInputWrongBorder
import com.shadowings.gero.presentation.ui.testModeCorrect
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.placeholder_write_translation
import gero.composeapp.generated.resources.question_type_translate
import gero.composeapp.generated.resources.quiz_ripasso_traslitterazione
import gero.composeapp.generated.resources.quiz_solution_prefix
import gero.composeapp.generated.resources.quiz_transliteration_entered_hint
import gero.composeapp.generated.resources.share_quiz_translate
import gero.composeapp.generated.resources.word_bank_object_plural
import gero.composeapp.generated.resources.word_bank_object_singular
import gero.composeapp.generated.resources.word_bank_subject_plural
import gero.composeapp.generated.resources.word_bank_subject_singular
import org.jetbrains.compose.resources.stringResource

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun TrueFalseContentComposablePreview() {
    AppTheme {
        Surface {
            TextTranslationComposable(
                quiz = TextTranslationContent(
                    id = "preview",
                    hieroglyphs = "G23-N5-X1:F23",
                    correctPhrase = "Acqua",
                    transliteration = "ꜥꜣꜥ"
                ),
                comboCount = mutableStateOf(0),
                quizList = mutableStateOf(listOf()),
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizResults = mutableStateOf(mapOf()),
                errorsCount = mutableStateOf(0),
                testMode = false,
                userData = UserData(),
            )
        }
    }
}

@Composable
fun TextTranslationComposable(
    quiz: TextTranslationContent,
    comboCount: MutableState<Int>,
    quizList: MutableState<List<IQuizContent>>,
    page: Int,
    pagerState: PagerState,
    navController: NavController,
    quizResults: MutableState<Map<String, Boolean>>,
    errorsCount: MutableState<Int>,
    testMode: Boolean,
    userData: UserData,
    lessonId: String = "",
    onComboFiveReached: (() -> Unit)? = null,
    onNextQuestion: ((isCorrect: Boolean) -> Unit)? = null,
    onAnswerResult: ((isCorrect: Boolean, record: WrongAnswerRecord) -> Unit)? = null
) {
    val state = rememberQuizPageState(
        quizId = quiz.id,
        page = page,
        pagerState = pagerState,
        navController = navController,
        quizResults = quizResults,
        quizList = quizList,
        comboCount = comboCount,
        errorsCount = errorsCount,
        onComboFiveReached = onComboFiveReached,
        onNextQuestion = onNextQuestion,
    )
    val graphicsLayer = rememberGraphicsLayer()

    var typedText by rememberSaveable(quiz.id) { mutableStateOf("") }
    val isRtl = remember { kotlin.random.Random.nextBoolean() }
    val deviceInfo = rememberDeviceInfo()
    val isLandscape = deviceInfo.isLandscape

    Box(modifier = Modifier.fillMaxSize()) {
        run {
            val questionTitle = if (quiz.soggettiCount != null && quiz.oggettiCount != null) {
                val subjectsText = if (quiz.soggettiCount == 1) {
                    stringResource(Res.string.word_bank_subject_singular)
                } else {
                    stringResource(Res.string.word_bank_subject_plural, quiz.soggettiCount)
                }
                val objectsText = if (quiz.oggettiCount == 1) {
                    stringResource(Res.string.word_bank_object_singular)
                } else {
                    stringResource(Res.string.word_bank_object_plural, quiz.oggettiCount)
                }
                stringResource(Res.string.question_type_translate) + " ($subjectsText, $objectsText)"
            } else {
                stringResource(Res.string.question_type_translate)
            }
            val wordContent: @Composable () -> Unit = {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .drawWithContent {
                            graphicsLayer.record {
                                this@drawWithContent.drawContent()
                            }
                            drawContent()
                        },
                    contentAlignment = Alignment.Center
                ) {
                    MdCWord(
                        word = quiz.hieroglyphs,
                        rtl = isRtl,
                        cart = false,
                        size = 56.dp,
                        testTag = quiz.hieroglyphs
                    )
                }
            }

            val questionContent = @Composable {
                QuestionBox(
                    question = questionTitle,
                    modifier = if (isLandscape) Modifier else Modifier.padding(vertical = 16.dp),
                    questionTestTag = "text_translation_question",
                    wordContent = wordContent
                )
            }

            val optionsContent = @Composable {
                val fieldPadding = if (isLandscape) 8.dp else 24.dp
                val solutionSpacerHeight = if (isLandscape) 8.dp else 16.dp
                val solutionCardPadding = if (isLandscape) 8.dp else 12.dp

                val borderCol = when {
                    state.showResult && state.correct -> quizInputCorrectBorder
                    state.showResult && !state.correct -> quizInputWrongBorder
                    else -> MaterialTheme.colorScheme.outline
                }

                OutlinedTextField(
                    value = typedText,
                    onValueChange = { if (state.pending) typedText = it },
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = fieldPadding)
                        .semantics { testTag = "text_translation_input" },
                    enabled = state.pending,
                    placeholder = { Text(stringResource(Res.string.placeholder_write_translation)) },
                    textStyle = MaterialTheme.typography.bodyLarge.copy(
                        textAlign = TextAlign.Start,
                        fontWeight = FontWeight.Medium
                    ),
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = borderCol,
                        unfocusedBorderColor = borderCol,
                        disabledBorderColor = borderCol,
                        focusedTextColor = MaterialTheme.colorScheme.onSurface,
                        unfocusedTextColor = MaterialTheme.colorScheme.onSurface,
                        disabledTextColor = MaterialTheme.colorScheme.onSurface
                    ),
                    singleLine = true
                )

                if (testMode) {
                    Spacer(modifier = Modifier.height(solutionSpacerHeight))
                    OutlinedCard(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = fieldPadding),
                        colors = CardDefaults.outlinedCardColors(
                            containerColor = testModeCorrect.copy(alpha = 0.1f)
                        ),
                        border = BorderStroke(1.dp, testModeCorrect)
                    ) {
                        Text(
                            text = stringResource(
                                Res.string.quiz_solution_prefix,
                                quiz.correctPhrase
                            ),
                            style = MaterialTheme.typography.bodyMedium.copy(
                                fontWeight = FontWeight.Bold
                            ),
                            color = testModeCorrect,
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(solutionCardPadding),
                            textAlign = TextAlign.Center
                        )
                    }
                }
            }

            QuizContentScaffold(
                testTag = "text_translation_content",
                isLandscape = isLandscape,
                landscapeQuestionScrollable = false,
                landscapeOptionsWeight = 1.2f,
                landscapeOptionsArrangement = Arrangement.Center,
                landscapeOptionsHorizontalAlignment = Alignment.CenterHorizontally,
                landscapeOptionsScrollable = false,
                portraitOptionsArrangement = Arrangement.spacedBy(16.dp, Alignment.CenterVertically),
                portraitOptionsScrollable = false,
                portraitOptionsHorizontalPadding = 0.dp,
                questionContent = { questionContent() },
                optionsContent = { optionsContent() }
            )
        }

        val quizInfo = """
            Quiz ID: ${quiz.id}
            Type: Text Translation Quiz
            Hieroglyphs: ${quiz.hieroglyphs}
            Transliteration (Reading): ${quiz.transliteration}
            Expected Translation: ${quiz.correctPhrase}
        """.trimIndent()
        val userAnswer = typedText

        val shareText = stringResource(Res.string.share_quiz_translate)

        val feedbackInfo = quiz.transliteration?.let { transliteration ->
            if (normalize(typedText) == normalize(transliteration)) {
                stringResource(
                    Res.string.quiz_transliteration_entered_hint,
                    transliteration
                )
            } else {
                stringResource(
                    Res.string.quiz_ripasso_traslitterazione,
                    transliteration
                )
            }
        }

        QuizBottomFeedbackAndButton(
            modifier = Modifier.align(Alignment.BottomCenter),
            state = state,
            buttonEnabled = typedText.isNotBlank(),
            info = feedbackInfo,
            correctAnswer = quiz.correctPhrase,
            userData = userData,
            lessonId = lessonId,
            quizInfo = quizInfo,
            userAnswer = userAnswer,
            shareText = shareText,
            shareImageProvider = {
                try {
                    graphicsLayer.toImageBitmap()
                } catch (e: Exception) {
                    null
                }
            },
            onConfirmClick = {
                val isCorrect = isTranslationMatch(typedText, quiz.correctPhrase)
                onAnswerResult?.invoke(
                    isCorrect,
                    WrongAnswerRecord(
                        id = WrongAnswerRecord.buildId(lessonId, quiz.hieroglyphs),
                        lessonId = lessonId,
                        type = QuizQuestionType.TEXT_TRANSLATION,
                        questionText = quiz.transliteration ?: quiz.hieroglyphs,
                        correctAnswerText = quiz.correctPhrase,
                        userAnswerText = userAnswer,
                        hieroglyphs = quiz.hieroglyphs,
                        subjectsCount = quiz.soggettiCount,
                        objectsCount = quiz.oggettiCount
                    )
                )
                isCorrect
            }
        )
    }
}

internal fun isTranslationMatch(userAnswer: String, expectedPhrase: String): Boolean {
    val normUser = cleanSpaces(expandEnglishContractions(normalize(userAnswer)))
    if (normUser.isBlank()) return false
    val accepted = getAcceptedAnswerVariants(expectedPhrase)
    if (normUser in accepted) return true

    val normUserWithoutParens = cleanSpaces(normUser.replace("(", "").replace(")", ""))
    if (normUserWithoutParens in accepted) return true

    val normUserWithoutDots = cleanSpaces(normUserWithoutParens.replace(".", ""))
    return normUserWithoutDots in accepted
}

internal fun getAcceptedAnswerVariants(phrase: String): Set<String> {
    val normalized = expandEnglishContractions(normalize(phrase))
    val regex = Regex("\\([^)]*\\)")
    val matches = regex.findAll(normalized).toList()
    if (matches.isEmpty()) {
        val basic = cleanSpaces(normalized)
        return setOf(basic, cleanSpaces(basic.replace(".", "")))
    }

    val results = mutableSetOf<String>()
    val n = matches.size
    val totalCombos = 1 shl n

    for (mask in 0 until totalCombos) {
        var lastIdx = 0
        val sb = StringBuilder()
        for (i in 0 until n) {
            val match = matches[i]
            sb.append(normalized.substring(lastIdx, match.range.first))
            val keep = (mask and (1 shl i)) != 0
            if (keep) {
                sb.append(match.value)
            }
            lastIdx = match.range.last + 1
        }
        sb.append(normalized.substring(lastIdx))

        val variantWithParens = cleanSpaces(sb.toString())
        if (variantWithParens.isNotBlank()) {
            results.add(variantWithParens)
            val variantWithoutParensChars =
                cleanSpaces(variantWithParens.replace("(", "").replace(")", ""))
            if (variantWithoutParensChars.isNotBlank()) {
                results.add(variantWithoutParensChars)
                val variantWithoutDots = cleanSpaces(variantWithoutParensChars.replace(".", ""))
                if (variantWithoutDots.isNotBlank()) {
                    results.add(variantWithoutDots)
                }
            }
        }
    }

    return results
}

internal fun cleanSpaces(s: String): String {
    return s.replace(Regex("\\s+"), " ").trim()
}

// A serial/Oxford comma before the last item's conjunction (e.g. "father, mother, and grain"
// vs. this app's own "father, mother and grain") is a stylistic choice, not a wrong answer —
// strip it so both forms compare equal.
private val serialCommaRegex = Regex(",\\s+(and|e|und|y|et)\\b")

// English negative contractions ("don't", "wasn't") are the same answer as the
// full forms the app generates ("do not", "was not"). Only n't forms: 's/'d/'ve
// are ambiguous (is/has, had/would, possessive). "can not" and "can't" both
// collapse to "cannot". These forms don't exist in the other UI languages, so
// no language check is needed. Expects already-normalized input (plain ').
private val englishContractions = mapOf(
    "don't" to "do not", "doesn't" to "does not", "didn't" to "did not",
    "isn't" to "is not", "aren't" to "are not", "wasn't" to "was not", "weren't" to "were not",
    "hasn't" to "has not", "haven't" to "have not", "hadn't" to "had not",
    "won't" to "will not", "can't" to "cannot", "couldn't" to "could not",
    "shouldn't" to "should not", "wouldn't" to "would not", "mustn't" to "must not",
)
private val englishContractionRegex =
    Regex("\\b(" + englishContractions.keys.joinToString("|") { Regex.escape(it) } + ")(?![\\w'])")
private val canNotRegex = Regex("\\bcan not\\b")

internal fun expandEnglishContractions(s: String): String {
    return s.replace(englishContractionRegex) { englishContractions.getValue(it.value) }
        .replace(canNotRegex, "cannot")
}

internal fun normalize(s: String): String {
    return s.replace("’", "'")
        .replace("`", "'")
        .replace("‘", "'")
        .replace("“", "\"")
        .replace("”", "\"")
        .replace(" '", "'")
        .replace("' ", "'")
        .trim()
        .lowercase()
        .replace(serialCommaRegex, " $1")
        .trimEnd('.', '!', '?', ';', ',')
}
